
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useCropDetails } from "@/hooks/useCropDetails";
import { Loader2, Sprout } from "lucide-react";

interface CropDetailsFormProps {
  onSave: (data: any) => void;
  initialData?: {
    crop: string;
    cropVariety: string;
    cropGroup: string;
  } | null;
}

export const CropDetailsForm = ({ onSave, initialData }: CropDetailsFormProps) => {
  const { isLoading, cropOptions, getCropTypesByGroup, getCropVarietiesByGroupAndType } = useCropDetails();
  
  const [crop, setCrop] = useState("");
  const [cropVariety, setCropVariety] = useState("");
  const [cropGroup, setCropGroup] = useState("");

  // Update form fields when initialData changes
  useEffect(() => {
    if (initialData) {
      console.log("🌱 CropDetailsForm: Setting initial data:", initialData);
      setCrop(initialData.crop || "");
      setCropVariety(initialData.cropVariety || "");
      setCropGroup(initialData.cropGroup || "");
    } else {
      // Clear form when no initial data
      setCrop("");
      setCropVariety("");
      setCropGroup("");
    }
  }, [initialData]);

  // Automatically trigger onSave when initial data is set
  useEffect(() => {
    if (initialData && crop && cropGroup) {
      const formData = { crop, cropVariety, cropGroup };
      console.log("🌱 CropDetailsForm: Auto-saving initial data:", formData);
      onSave(formData);
    }
  }, [crop, cropVariety, cropGroup, initialData, onSave]);

  const cropTypes = getCropTypesByGroup(cropGroup);
  const cropVarieties = getCropVarietiesByGroupAndType(cropGroup, crop);

  const handleSave = () => {
    const formData = { crop, cropVariety, cropGroup };
    console.log("🌱 CropDetailsForm: Manual save:", formData);
    onSave(formData);
  };

  const isFormValid = crop && cropGroup;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-green-800 flex items-center">
            <Sprout className="mr-2" size={20} />
            Crop Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-green-600" />
            <span className="ml-2 text-gray-600">Loading crop data...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-green-800 flex items-center">
          <Sprout className="mr-2" size={20} />
          Crop Details
          {initialData && (
            <span className="ml-2 text-sm font-normal text-green-600 bg-green-50 px-2 py-1 rounded">
              Auto-filled from plan
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="cropGroup">Crop Group *</Label>
            <Select value={cropGroup} onValueChange={setCropGroup}>
              <SelectTrigger>
                <SelectValue placeholder="Select crop group" />
              </SelectTrigger>
              <SelectContent>
                {(() => {
                  console.log("🎯 CropDetailsForm: Rendering groups:", {
                    groupsCount: cropOptions.groups.length,
                    groups: cropOptions.groups
                  });
                  return cropOptions.groups.map((group) => (
                    <SelectItem key={group} value={group}>
                      {group}
                    </SelectItem>
                  ));
                })()}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="crop">Crop Type *</Label>
            <Select value={crop} onValueChange={setCrop} disabled={!cropGroup}>
              <SelectTrigger>
                <SelectValue placeholder={cropGroup ? "Select crop type" : "Select group first"} />
              </SelectTrigger>
              <SelectContent>
                {cropTypes.map((cropType) => (
                  <SelectItem key={cropType} value={cropType}>
                    {cropType}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="cropVariety">Crop Variety</Label>
          <Select value={cropVariety} onValueChange={setCropVariety} disabled={!crop}>
            <SelectTrigger>
              <SelectValue placeholder={crop ? "Select variety (optional)" : "Select crop type first"} />
            </SelectTrigger>
            <SelectContent>
              {cropVarieties.map((variety) => (
                <SelectItem key={variety} value={variety}>
                  {variety}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!initialData && (
          <Button 
            onClick={handleSave} 
            disabled={!isFormValid}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            Save Crop Details
          </Button>
        )}

        {initialData && (
          <div className="text-sm text-green-700 bg-green-50 p-3 rounded-lg">
            <p className="font-medium">✓ Crop details auto-filled from your crop plan</p>
            <p className="text-xs mt-1">You can still modify the details or save manually if needed</p>
            {!isFormValid && (
              <Button 
                onClick={handleSave} 
                disabled={!isFormValid}
                size="sm"
                className="mt-2 bg-green-600 hover:bg-green-700"
              >
                Save Details
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
